"use client";

import { useState, useMemo } from "react";
import { Icon } from "@iconify/react";
import { AnimatePresence, motion } from "motion/react";
import ProductSpecsTable from "./ProductSpecsTable";
import ProductAccessories from "./ProductAccessories";
import type { UnifiedProduct } from "@/lib/product-data/product.types";

export default function ProductTabs({ product }: { product: UnifiedProduct }) {
  // We build a deterministic tabs order
  const baseSections = [
    "Tech Specs",
    "Main Characteristics",
    "connectivity",
    "Physical Characteristics",
    "accessories",
  ];
  const available = Object.keys(product.sections || {});

  const tabs = useMemo(() => {
    const seen = new Set<string>();
    const out: { id: string; label: string; icon: string }[] = [];

    // preferred order
    baseSections.forEach((key) => {
      if (available.includes(key)) {
        seen.add(key);
        out.push({
          id: key,
          label: key.charAt(0).toUpperCase() + key.slice(1).replace("-", " "),
          icon:
            key === "Tech Specs"
              ? "ion:settings-outline"
              : key === "Main Characteristics"
              ? "ion:hardware-chip-outline"
              : key === "Physical Characteristics"
              ? "ion:cube-outline"
              : key === "accessories"
              ? "ion:briefcase-outline"
              : key === "connectivity"
              ? "ion:wifi-outline"
              : "ion:information-circle-outline",
        });
      }
    });

    // add any other keys that aren't in baseSections
    available.forEach((k) => {
      if (!seen.has(k)) {
        out.push({
          id: k,
          label: k.charAt(0).toUpperCase() + k.slice(1).replace("-", " "),
          icon: "ion:information-circle-outline",
        });
      }
    });

    // if pdf exists, add downloads tab last
    if (product.pdfUrl) {
      out.push({
        id: "downloads",
        label: "Downloads",
        icon: "ion:cloud-download-outline",
      });
    }

    return out;
  }, [product.sections, product.pdfUrl, available]);

  const defaultTab = tabs.length > 0 ? tabs[0].id : "Tech Specs";
  const [activeTab, setActiveTab] = useState<string>(defaultTab);

  return (
    <>
      <div className="flex flex-wrap gap-2 border-b border-border dark:border-white/10 mb-10 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-bold uppercase tracking-wider transition-all border-b-2 whitespace-nowrap
              ${
                activeTab === tab.id
                  ? "border-primary text-primary bg-primary/5"
                  : "border-transparent text-muted dark:text-white/60 hover:text-midnight_text"
              }`}
          >
            <Icon icon={tab.icon} width="18" />
            {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}
          className="bg-white dark:bg-white/5 rounded-3xl p-8 md:p-12 border border-border dark:border-white/10 shadow-sm"
        >
          {activeTab === "accessories" &&
            Array.isArray(product.sections[activeTab]) && (
              <ProductAccessories
                accessories={product.sections[activeTab] as any}
              />
            )}

          {activeTab === "downloads" && product.pdfUrl && (
            <div className="flex flex-col gap-4">
              <a
                href={product.pdfUrl}
                download
                target="_blank"
                className="flex items-center justify-between p-6 bg-hero dark:bg-black/20 rounded-2xl border border-border dark:border-white/10 hover:border-primary/50 transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-50 dark:bg-white/5 rounded-xl flex items-center justify-center text-red-500 group-hover:scale-110 transition-transform">
                    <Icon icon="ion:document-text" width="24" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg text-midnight_text dark:text-white group-hover:text-primary transition-colors">
                      Product Datasheet
                    </h4>
                    <p className="text-sm text-muted dark:text-white/60">
                      PDF Document - Technical Specifications
                    </p>
                  </div>
                </div>
                <div className="bg-primary text-white p-3 rounded-xl opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">
                  <Icon icon="ion:download-outline" width="24" />
                </div>
              </a>
            </div>
          )}

          {activeTab !== "accessories" &&
            activeTab !== "downloads" &&
            typeof product.sections[activeTab] === "object" && (
              <ProductSpecsTable
                data={product.sections[activeTab] as Record<string, string>}
              />
            )}
        </motion.div>
      </AnimatePresence>
    </>
  );
}
